import { useEffect, useState, useMemo, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { MagnifyingGlass, FunnelSimple, X } from "@phosphor-icons/react";
import { Drawer, DrawerContent, DrawerTitle } from "../components/ui/drawer";
import api from "../lib/api";
import { TxRow } from "../components/TxRow";
import { TX_META } from "../lib/format";

export default function Transactions() {
  const [searchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [q, setQ] = useState("");
  const [filters, setFilters] = useState({ status: searchParams.get("status") || "", transaction_type: "", account_id: "", category_id: "", date_from: "", date_to: "" });
  const [filterOpen, setFilterOpen] = useState(false);
  const [lookups, setLookups] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/lookups").then((r) => setLookups(r.data)).catch(() => {});
  }, []);

  const load = useCallback(async (append = false, skip = 0) => {
    setLoading(true);
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    Object.entries(filters).forEach(([k, v]) => v && params.set(k, v));
    params.set("limit", "50");
    params.set("skip", String(skip));
    try {
      const { data } = await api.get(`/transactions?${params}`);
      setItems((prev) => (append ? [...prev, ...data.items] : data.items));
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  }, [q, filters]);

  useEffect(() => {
    const t = setTimeout(() => load(), 250);
    return () => clearTimeout(t);
  }, [load]);

  const catNames = useMemo(() => Object.fromEntries((lookups?.categories || []).map((c) => [c.id, c.name])), [lookups]);
  const activeFilters = Object.values(filters).filter(Boolean).length;
  const sel = "w-full h-11 px-3 rounded-xl bg-background border border-border text-sm";

  return (
    <div className="px-5 pt-8" data-testid="transactions-page">
      <h1 className="text-2xl font-heading font-bold tracking-tight">Activity</h1>
      <div className="mt-4 flex gap-2">
        <div className="flex-1 relative">
          <MagnifyingGlass size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <input data-testid="tx-search-input" className="w-full h-12 pl-10 pr-4 rounded-xl bg-white border border-border" placeholder="Search merchant or notes" value={q} onChange={(e) => setQ(e.target.value)} />
        </div>
        <button data-testid="tx-filter-btn" onClick={() => setFilterOpen(true)} className="h-12 w-12 rounded-xl bg-white border border-border flex items-center justify-center relative">
          <FunnelSimple size={18} />
          {activeFilters > 0 && <span className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-primary text-primary-foreground text-[10px] flex items-center justify-center">{activeFilters}</span>}
        </button>
      </div>

      <div className="mt-4 bg-white border border-border rounded-2xl px-4 py-1 min-h-[120px]">
        {loading && items.length === 0 ? (
          <div className="py-8 flex justify-center"><div className="h-5 w-5 rounded-full border-2 border-primary border-t-transparent animate-spin" /></div>
        ) : items.length === 0 ? (
          <p className="py-8 text-sm text-muted-foreground text-center">No transactions match.</p>
        ) : (
          items.map((tx) => <TxRow key={tx.id} tx={tx} categories={catNames} />)
        )}
      </div>
      {items.length < total && (
        <button data-testid="tx-load-more" onClick={() => load(true, items.length)} className="mt-4 w-full h-11 rounded-xl border border-border bg-white text-sm font-medium">
          Load more ({items.length} of {total})
        </button>
      )}

      <Drawer open={filterOpen} onOpenChange={setFilterOpen}>
        <DrawerContent className="max-w-md mx-auto rounded-t-3xl bg-white">
          <DrawerTitle className="px-5 pt-2 pb-3 text-base font-heading font-semibold flex items-center justify-between">
            Filters
            <button data-testid="tx-filter-clear" onClick={() => setFilters({ status: "", transaction_type: "", account_id: "", category_id: "", date_from: "", date_to: "" })} className="text-xs font-normal text-muted-foreground flex items-center gap-1"><X size={12} /> Clear all</button>
          </DrawerTitle>
          <div className="px-5 pb-8 space-y-3 overflow-y-auto max-h-[65vh]">
            <select data-testid="filter-type" className={sel} value={filters.transaction_type} onChange={(e) => setFilters({ ...filters, transaction_type: e.target.value })}>
              <option value="">All types</option>
              {Object.entries(TX_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
            <select data-testid="filter-status" className={sel} value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })}>
              <option value="">All statuses</option>
              <option value="posted">Posted</option>
              <option value="draft">Draft</option>
              <option value="voided">Voided</option>
            </select>
            <select data-testid="filter-account" className={sel} value={filters.account_id} onChange={(e) => setFilters({ ...filters, account_id: e.target.value })}>
              <option value="">All accounts</option>
              {(lookups?.accounts || []).map((a) => <option key={a.id} value={a.id}>{a.name}</option>)}
            </select>
            <select data-testid="filter-category" className={sel} value={filters.category_id} onChange={(e) => setFilters({ ...filters, category_id: e.target.value })}>
              <option value="">All categories</option>
              {(lookups?.categories || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <div className="grid grid-cols-2 gap-2">
              <input data-testid="filter-date-from" type="date" className={sel} value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} />
              <input data-testid="filter-date-to" type="date" className={sel} value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} />
            </div>
            <button data-testid="filter-apply" onClick={() => setFilterOpen(false)} className="w-full h-12 rounded-xl bg-primary text-primary-foreground font-medium">Apply</button>
          </div>
        </DrawerContent>
      </Drawer>
    </div>
  );
}
